import React, { useState, useEffect } from 'react';
import usePersistentState from '../hooks/usePersistentState';
import useDebounce from '../hooks/useDebounce';
import FilterPanel from '../components/FilterPanel';
import DataTable from '../components/DataTable';
import dataService from '../services/dataService';
import exportService from '../services/exportService';
import pdfExportService from '../services/pdfExportService';
import UpdateNotification from '../components/UpdateNotification';
import LoadingSpinner from '../components/LoadingSpinner';

const OrdersList = ({ initialFilters = {} }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [filters, setFilters] = usePersistentState('ordersList.filters', initialFilters);
  const debouncedFilters = useDebounce(filters, 300);
  const [sortField, setSortField] = usePersistentState('ordersList.sortField', 'Data Inclusao');
  const [sortDirection, setSortDirection] = usePersistentState('ordersList.sortDirection', 'desc');
  const [currentPage, setCurrentPage] = usePersistentState('ordersList.currentPage', 1);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'info' });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Aplicar filtros quando os filtros mudarem
    applyFilters();
  }, [debouncedFilters, data]);

  useEffect(() => {
    const sortData = () => {
      const dataToSort = [...filteredData];
      dataToSort.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Tratamento especial para datas
        if (sortField === 'Data Inclusao') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        }

        // Tratamento para números
        if (sortField === 'Vlr Total orçado') {
          const toNum = (v) => parseFloat(v) || 0;
          aValue = toNum(a['Vlr.cobrado ser']) + toNum(a['Vlr.cobrado mat']) + toNum(a['Vlr.cobrado itens']);
          bValue = toNum(b['Vlr.cobrado ser']) + toNum(b['Vlr.cobrado mat']) + toNum(b['Vlr.cobrado itens']);
        } else if (sortField.includes('Vlr.')) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else if (sortField === 'Ord.Ser') {
          const toNum = (v) => {
            if (v === null || v === undefined) return -Infinity;
            const m = String(v).match(/\d+/);
            return m ? parseInt(m[0], 10) : -Infinity;
          };
          aValue = toNum(aValue);
          bValue = toNum(bValue);
        }

        // Tratamento para strings
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
      setSortedData(dataToSort);
    };

    sortData();
  }, [filteredData, sortField, sortDirection]);

  useEffect(() => {
    // Aplicar filtros iniciais se fornecidos
    if (Object.keys(initialFilters).length > 0) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Usar loadData em vez de getAllData para garantir que as permissões 
      // do usuário atual sejam aplicadas corretamente
      const loadedData = await dataService.loadData();
      setData(loadedData);
      setFilteredData(loadedData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setToast({ message: 'Erro ao carregar dados. Tente atualizar a página.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    if (data.length === 0) return;
    
    const filtered = dataService.filterData(debouncedFilters);
    setFilteredData(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleFilterReset = () => {
    setFilters({});
    dataService.resetFilters();
    setFilteredData(data);
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleExportCSV = () => {
    if (filteredData.length === 0) {
      setToast({ message: 'Não há dados para exportar!', type: 'warning' });
      return;
    }
    
    const filename = Object.keys(filters).length > 0 ? 
      'ordens_servico_filtradas' : 
      'ordens_servico_completas';
    
    try {
      exportService.exportToCSV(filteredData, filename);
      setToast({ message: 'Relatório gerado com sucesso! Verifique a pasta de downloads.', type: 'success' });
    } catch (error) {
      setToast({ message: 'Erro ao gerar relatório CSV: ' + error.message, type: 'error' });
    }
  };

  const handleExportPDF = async () => {
    if (filteredData.length === 0) {
      setToast({ message: 'Não há dados para exportar!', type: 'warning' });
      return;
    }
    
    const filename = Object.keys(filters).length > 0 ? 
      'ordens_servico_filtradas' : 
      'ordens_servico_completas';
    
    try {
      const ordersForReport = filteredData.map(o => ({
        id: o['Ord.Ser'] ?? '',
        cliente: dataService.safeValue(o['Cliente']),
        status: dataService.safeValue(o['Posição']),
        tecnico: dataService.safeValue(o['Técnico']),
        data: dataService.formatDate(o['Data Inclusao'])
      }));
      await pdfExportService.exportOrdersReport(ordersForReport, filters);
      setToast({ message: 'Relatório gerado com sucesso! Verifique a pasta de downloads.', type: 'success' });
    } catch (error) {
      setToast({ message: 'Erro ao gerar relatório: ' + error.message, type: 'error' });
    }
  };

  

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => 
      value && value !== '' && value.toString().trim() !== ''
    ).length;
  };

  const getFiltersSummary = () => {
    const activeFilters = [];
    
    if (filters.status) {
      activeFilters.push(`Status: ${filters.status}`);
    }
    if (filters.faturada) {
      activeFilters.push(`Faturamento: ${filters.faturada === 'sim' ? 'Faturada' : 'Não Faturada'}`);
    }
    if (filters.problema) {
      activeFilters.push(`Problema: "${filters.problema}"`);
    }
    if (filters.tecnico) {
      activeFilters.push(`Técnico: "${filters.tecnico}"`);
    }
    if (filters.dataInicio) {
      activeFilters.push(`Data início: ${new Date(filters.dataInicio).toLocaleDateString('pt-BR')}`);
    }
    if (filters.dataFim) {
      activeFilters.push(`Data fim: ${new Date(filters.dataFim).toLocaleDateString('pt-BR')}`);
    }
    if (filters.busca) {
      activeFilters.push(`Busca: "${filters.busca}"`);
    }
    
    return activeFilters;
  };

  return (
    <div className="space-y-6">
      {isLoading && <LoadingSpinner />}
      {toast.message && (
        <UpdateNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'info' })}
        />
      )}
      {/* Header com informações */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-dark-text">Lista de Ordens de Serviço</h2>
          <div className="flex items-center gap-4 text-dark-text-secondary text-sm mt-1">
            <span>Total: {data.length} ordens</span>
            <span>Filtradas: {filteredData.length} ordens</span>
            {getActiveFiltersCount() > 0 && (
              <span className="text-primary font-medium">
                {getActiveFiltersCount()} filtro(s) ativo(s)
              </span>
            )}
          </div>
        </div>
        
      </div>

      {/* Resumo dos filtros ativos */}
      {getActiveFiltersCount() > 0 && (
        <div className="card-dark p-4">
          <h3 className="text-sm font-medium text-dark-text mb-2">Filtros Ativos:</h3>
          <div className="flex flex-wrap gap-2">
            {getFiltersSummary().map((filter, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary text-white"
              >
                {filter}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleFilterReset}
      />

      {/* Tabela */}
      {data.length > 0 ? (
        <DataTable
          data={sortedData}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onExportPDF={handleExportPDF}
          onExportCSV={handleExportCSV}
        />
      ) : (
        <div className="card-dark p-8 text-center">
          <div className="text-dark-text-secondary mb-4">
            <div>
              <p className="text-lg mb-2">Nenhuma ordem de serviço encontrada</p>
              <p className="text-sm">
                Verifique se os dados foram carregados corretamente ou tente ajustar os filtros.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Informações adicionais */}
      {filteredData.length === 0 && data.length > 0 && !isLoading && (
        <div className="card-dark p-6 border-l-4 border-warning">
          <h3 className="text-lg font-medium text-dark-text mb-2">
            Nenhum resultado encontrado
          </h3>
          <p className="text-dark-text-secondary mb-4">
            Os filtros aplicados não retornaram nenhum resultado. Tente:
          </p>
          <ul className="text-dark-text-secondary text-sm space-y-1 ml-4">
            <li>• Remover alguns filtros</li>
            <li>• Usar termos mais genéricos na busca</li>
            <li>• Verificar as datas selecionadas</li>
          </ul>
          <button onClick={handleFilterReset} className="btn-primary mt-4">Limpar todos os filtros</button>
        </div>
      )}
    </div>
  );
};

export default OrdersList; 
